import React, { useEffect, useState } from "react";
import { Gift, X, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function BonanzaPopup() {
  const [ev, setEv] = useState(null);
  const [scratched, setScratched] = useState(false);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => {
    api.get("/bonanza/my").then((r) => {
      const list = r.data.bonanzas || [];
      const pending = list.find((b) => b.status === "QUALIFIED" && !b.seen) || list.find((b) => b.status === "QUALIFIED");
      if (pending) { setEv(pending); const hasCash = pending.reward_cash_value; const hasGift = pending.reward_gift_label; setChoice(hasCash && hasGift ? "" : hasGift ? "gift" : "cash"); }
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!ev) return null;
  const hasCash = ev.reward_cash_value != null && ev.reward_cash_value !== 0;
  const hasGift = !!ev.reward_gift_label;
  const needsChoice = hasCash && hasGift;

  const dismiss = async () => { try { await api.post(`/bonanza/${ev.bonanza_id}/seen`); } catch { /* noop */ } setEv(null); };

  const claim = async () => {
    if (needsChoice && !choice) return toast.error("Choose Cash or Gift");
    setBusy(true);
    try {
      const { data } = await api.post(`/bonanza/${ev.bonanza_id}/claim`, { choice });
      setResult(data);
      if (data.choice === "cash") toast.success(`$${fmt(data.reward_cash_value)} credited to your main wallet!`);
      else toast.success("Gift claim submitted — admin will process it.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Claim failed"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/80 p-4" data-testid="bonanza-popup">
      <div className="relative w-full max-w-sm overflow-hidden rounded-3xl p-6 text-center"
        style={{ background: "linear-gradient(160deg,#2a0a3a,#16032a)", border: "1px solid rgba(236,72,153,.5)", boxShadow: "0 0 60px rgba(236,72,153,.35)" }}>
        <button onClick={dismiss} data-testid="bonanza-close" className="absolute right-3 top-3 text-white/60 hover:text-white"><X size={18} /></button>
        <div className="mx-auto mb-2 grid h-14 w-14 place-items-center rounded-full bg-[#ec4899]/20"><Gift size={26} className="text-[#ec4899]" /></div>
        <h3 className="text-lg font-extrabold text-white">🎉 {ev.bonanza_name}</h3>
        <p className="mt-1 text-xs text-white/60">You qualified with your ${fmt(ev.amount)} deposit. Scratch to reveal your reward!</p>

        {result ? (
          <div className="mt-4 rounded-2xl bg-white/10 p-4" data-testid="bonanza-result">
            <Sparkles className="mx-auto text-amber-300" size={24} />
            {result.choice === "cash"
              ? <p className="mt-2 text-white">🎁 <b className="text-emerald-300">${fmt(result.reward_cash_value)}</b> credited to your <b>main wallet</b>!</p>
              : <p className="mt-2 text-white">🎁 Your <b className="text-[#f9a8d4]">{result.reward_gift_label}</b> claim is <b>being processed</b> by admin.</p>}
            <button onClick={() => setEv(null)} data-testid="bonanza-done" className="mt-4 w-full rounded-xl bg-[#ec4899] py-2.5 text-sm font-extrabold text-white hover:bg-[#db2777]">Awesome!</button>
          </div>
        ) : !scratched ? (
          <button onClick={() => setScratched(true)} data-testid="bonanza-scratch"
            className="mt-4 w-full rounded-2xl border-2 border-dashed border-[#f9a8d4]/60 bg-gradient-to-br from-[#c026d3]/40 to-[#7e22ce]/40 py-8 text-base font-extrabold text-white transition hover:from-[#c026d3]/60 hover:to-[#7e22ce]/60">
            ✨ SCRATCH HERE ✨
          </button>
        ) : (
          <div className="mt-4 space-y-3" data-testid="bonanza-reveal">
            <div className="rounded-2xl bg-white/10 p-3">
              {hasGift && <p className="text-sm text-white">🏆 <b className="text-[#f9a8d4]">{ev.reward_gift_label}</b></p>}
              {hasCash && <p className="text-sm text-white">💵 <b className="text-emerald-300">${fmt(ev.reward_cash_value)}</b> cash reward</p>}
            </div>
            {needsChoice && (
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setChoice("gift")} data-testid="bonanza-choice-gift" className={`rounded-xl border px-2 py-2 text-xs font-bold ${choice === "gift" ? "border-[#ec4899] bg-[#ec4899]/20 text-white" : "border-white/15 bg-white/5 text-white/70"}`}>Take Gift<br /><span className="text-[10px] font-normal">{ev.reward_gift_label}</span></button>
                <button onClick={() => setChoice("cash")} data-testid="bonanza-choice-cash" className={`rounded-xl border px-2 py-2 text-xs font-bold ${choice === "cash" ? "border-emerald-400 bg-emerald-500/20 text-white" : "border-white/15 bg-white/5 text-white/70"}`}>Take Cash<br /><span className="text-[10px] font-normal">${fmt(ev.reward_cash_value)}</span></button>
              </div>
            )}
            <button onClick={claim} disabled={busy} data-testid="bonanza-claim" className="w-full rounded-xl bg-[#ec4899] py-2.5 text-sm font-extrabold text-white hover:bg-[#db2777] disabled:opacity-50">{busy ? "Claiming…" : "Claim Reward"}</button>
          </div>
        )}
      </div>
    </div>
  );
}
